import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { publicApi } from '../lib/api';

const ConfigContext = createContext(null);

// Default configuration values (fallback)
const DEFAULT_CONFIG = {
  company_name: 'Mitonner',
  slogan: "Profitez de votre soirée, on s'occupe du dîner !",
  // Media / Logos
  logo_url: 'https://customer-assets.emergentagent.com/job_cooking-planner/artifacts/9loa2439_logo.png',
  logo_white_url: null,
  favicon_url: null,
  email_logo_url: null,
  pdf_logo_url: null,
  og_image_url: null,
  // Logo sizes (responsive)
  logo_height_desktop: 48,
  logo_height_tablet: 40,
  logo_height_mobile: 36,
  // Contact
  phone: '01 23 45 67 89',
  email: 'contact@mitonner-essentiel.fr',
  address: '',
  postal_code: '',
  city: '',
  opening_hours: 'Lundi - Vendredi: 9h - 18h',
  social_media: {
    facebook: null,
    instagram: null,
    linkedin: null,
    tiktok: null,
    youtube: null,
    pinterest: null,
    twitter: null
  },
  external_links: {
    urssaf_main: 'https://www.urssaf.fr',
    urssaf_avance_immediate: 'https://www.urssaf.fr/portail/home/particulier-employeur/avance-immediate.html',
    service_public: 'https://www.service-public.fr',
    sap_official: 'https://www.servicesalapersonne.gouv.fr'
  },
  seo: {
    site_title: 'Mitonner - Cuisine à domicile',
    meta_description: 'Service de cuisine à domicile. Un cuisinier passionné prépare 10 repas savoureux chez vous. Crédit d\'impôt 50% - Avance Immédiate URSSAF.',
    meta_keywords: 'cuisine à domicile, repas maison, cuisinier, service à la personne, SAP, crédit impôt',
    og_title: null,
    og_description: null,
    og_image: null
  },
  legal_texts: {
    legal_mentions_html: '',
    cgv_html: '',
    cgu_html: '',
    privacy_policy_html: '',
    cookies_policy_html: ''
  },
  sap_number: 'SAP102115565',
  sap_enabled: true,
  sap_logo_url: null,
  sap_logo_enabled: true,
  sap_declaration_text: 'Déclaration de services à la personne enregistrée sous le n°',
  sap_credit_rate: 50,
  sap_marketing_text: 'Bénéficiez de 50% de crédit d\'impôt sur nos prestations',
  footer_text: '© 2026 Mitonner - Tous droits réservés',
  logos: [],
  homepage_title: "Profitez de votre soirée, on s'occupe du dîner !",
  homepage_subtitle: 'Un cuisinier passionné vient à votre domicile préparer 10 repas savoureux et équilibrés. Gagnez du temps et régalez-vous toute la semaine.',
  // Images
  hero_image_url: null,
  about_image_url: null,
  vegetables_image_url: null,
  banner_image_url: null,
  default_recipe_image: null,
  maintenance_mode: false,
  maintenance_message: 'Le site est en maintenance. Nous revenons bientôt !'
};

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadConfig = useCallback(async () => {
    try {
      const response = await publicApi.getPublicConfig();
      const data = response.data || {};
      // Deep-merge des sous-objets pour préserver les valeurs par défaut (ex: seo:{})
      const deepKeys = ['seo', 'social_media', 'external_links', 'legal_texts', 'email_settings'];
      const merged = { ...DEFAULT_CONFIG, ...data };
      deepKeys.forEach((k) => {
        if (DEFAULT_CONFIG[k] && typeof DEFAULT_CONFIG[k] === 'object') {
          merged[k] = { ...DEFAULT_CONFIG[k], ...(data[k] || {}) };
        }
      });
      setConfig(merged);
      setError(null);
    } catch (err) {
      console.error('Error loading configuration:', err);
      setError(err);
      // Keep using default config on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Refresh config (useful after admin updates)
  const refreshConfig = useCallback(async () => {
    setLoading(true);
    await loadConfig();
  }, [loadConfig]);

  // Helper to get nested config value safely
  const getConfigValue = useCallback((path, defaultValue = '') => {
    const keys = path.split('.');
    let value = config;
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return defaultValue;
      }
    }
    return value ?? defaultValue;
  }, [config]);

  // Check if site is in maintenance mode
  const isMaintenanceMode = config.maintenance_mode && !window.location.pathname.startsWith('/admin');

  // Get logo height based on screen size
  const getLogoHeight = useCallback((screenWidth) => {
    if (screenWidth < 640) {
      return config.logo_height_mobile || 36;
    } else if (screenWidth < 1024) {
      return config.logo_height_tablet || 40;
    }
    return config.logo_height_desktop || 48;
  }, [config.logo_height_mobile, config.logo_height_tablet, config.logo_height_desktop]);

  // Get the appropriate logo URL (with fallback chain)
  const getLogoUrl = useCallback((variant = 'default') => {
    const fallback = 'https://customer-assets.emergentagent.com/job_cooking-planner/artifacts/9loa2439_logo.png';
    switch (variant) {
      case 'white':
        return config.logo_white_url || config.logo_url || fallback;
      case 'email':
        return config.email_logo_url || config.logo_url || fallback;
      case 'pdf':
        return config.pdf_logo_url || config.logo_url || fallback;
      default:
        return config.logo_url || fallback;
    }
  }, [config.logo_url, config.logo_white_url, config.email_logo_url, config.pdf_logo_url]);

  return (
    <ConfigContext.Provider value={{ 
      config, 
      loading, 
      error, 
      refreshConfig,
      getConfigValue,
      isMaintenanceMode,
      getLogoHeight,
      getLogoUrl
    }}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

// Export default config for reference
export { DEFAULT_CONFIG };
